import React, { useCallback, useEffect, useState } from 'react';
import { Clock, CheckCircle2, ChefHat, Bike, ShoppingBag, Eye, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Restaurant } from '../../lib/types';
import { supabase } from '../../lib/supabase';
interface OrderOption {
  id: string;
  order_item_id: string;
  option_group: string;
  option_name: string;
  price_delta: number;
}
interface OrderItem {
  id: string;
  order_id: string;
  item_name_snapshot: string;
  unit_price_snapshot: number;
  quantity: number;
  notes: string | null;
  line_total: number;
  status: string;
  options: OrderOption[];
}
interface CaprichoOrder {
  id: string;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  delivery_type: string;
  total: number;
  status: string;
  notes: string | null;
  created_at: string;
  delivery_date: string | null;
  delivery_time_slot: string | null;
  ticket_image_url: string | null;
  items: OrderItem[];
}
interface CaprichoKDSProps {
  restaurant: Restaurant;
}
export function CaprichoKDS({
  restaurant
}: CaprichoKDSProps) {
  const [orders, setOrders] = useState<CaprichoOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [now, setNow] = useState(new Date());
  const [ticketPreview, setTicketPreview] = useState<string | null>(null);
  // Timer update every 30s
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);
  // Fetch orders directly from Supabase
  const fetchOrders = useCallback(async () => {
    console.log('[CAPRICHO KDS] Fetching orders for restaurant:', restaurant.id);
    const {
      data: ordersData,
      error: ordersError
    } = await supabase.from('orders').select('*').eq('restaurant_id', restaurant.id).order('created_at', {
      ascending: false
    }).limit(50);
    if (ordersError) {
      console.error('[CAPRICHO KDS] Error fetching orders:', ordersError);
      return;
    }
    if (!ordersData || ordersData.length === 0) {
      setOrders([]);
      setLoading(false);
      return;
    }
    // Fetch order items
    const orderIds = ordersData.map((o: any) => o.id);
    const {
      data: itemsData,
      error: itemsError
    } = await supabase.from('order_items').select('*').in('order_id', orderIds);
    if (itemsError) {
      console.error('[CAPRICHO KDS] Error fetching items:', itemsError);
    }
    // Fetch options
    const itemIds = (itemsData || []).map((i: any) => i.id);
    let optionsData: any[] = [];
    if (itemIds.length > 0) {
      const {
        data: optData,
        error: optError
      } = await supabase.from('order_item_options').select('*').in('order_item_id', itemIds);
      if (optError) {
        console.error('[CAPRICHO KDS] Error fetching options:', optError);
      }
      optionsData = optData || [];
    }
    // Assemble
    const assembled: CaprichoOrder[] = ordersData.map((order: any) => {
      const orderItems = (itemsData || []).filter((item: any) => item.order_id === order.id).map((item: any) => ({
        ...item,
        options: optionsData.filter((opt: any) => opt.order_item_id === item.id)
      }));
      return {
        id: order.id,
        order_number: order.order_number,
        customer_name: order.customer_name,
        customer_phone: order.customer_phone,
        delivery_type: order.delivery_type,
        total: order.total,
        status: order.status,
        notes: order.notes,
        created_at: order.created_at,
        delivery_date: order.delivery_date || null,
        delivery_time_slot: order.delivery_time_slot || null,
        ticket_image_url: order.ticket_image_url || null,
        items: orderItems
      };
    });
    console.log('[CAPRICHO KDS] Fetched', assembled.length, 'orders. Statuses:', assembled.map(o => `${o.order_number}:${o.status}`));
    assembled.forEach(o => {
      console.log(`[CAPRICHO KDS] Order ${o.order_number} ticket_image_url:`, o.ticket_image_url || '(none)');
    });
    setOrders(assembled);
    setLoading(false);
  }, [restaurant.id]);
  // Initial fetch + polling every 10s
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);
  // Direct Supabase update
  const handleAction = async (orderId: string, action: string) => {
    if (action === 'cancel' && !confirm('¿Estás seguro de cancelar este pedido?')) return;
    const STATUS_MAP: Record<string, string> = {
      accept: 'accepted',
      prepare: 'preparing',
      ready: 'ready',
      deliver: 'delivered',
      cancel: 'cancelled'
    };
    const nextStatus = STATUS_MAP[action];
    const currentOrder = orders.find(o => o.id === orderId);
    const shortDisplay = currentOrder ? getShortOrderNumber(currentOrder.order_number) : '?';
    console.log('[CAPRICHO KDS] === ACTION DEBUG ===');
    console.log('[CAPRICHO KDS] order.id (real PK):', orderId);
    console.log('[CAPRICHO KDS] order.order_number:', currentOrder?.order_number);
    console.log('[CAPRICHO KDS] short display #:', shortDisplay);
    console.log('[CAPRICHO KDS] current status:', currentOrder?.status);
    console.log('[CAPRICHO KDS] next status:', nextStatus);
    console.log('[CAPRICHO KDS] action:', action);
    console.log('[CAPRICHO KDS] update will use .eq("id",', orderId, ')');
    if (!nextStatus) {
      console.error('[CAPRICHO KDS] Unknown action:', action);
      return;
    }
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    // Optimistic: move card immediately
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      status: nextStatus
    } : o));
    try {
      // 1. Update orders.status
      const payload = {
        status: nextStatus,
        updated_at: new Date().toISOString()
      };
      console.log('[CAPRICHO KDS] Supabase orders update payload:', payload);
      const {
        data,
        error
      } = await supabase.from('orders').update(payload).eq('id', orderId).select();
      if (error) {
        console.error('[CAPRICHO KDS] orders UPDATE FAILED:', {
          error: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        await fetchOrders();
      } else {
        console.log('[CAPRICHO KDS] orders UPDATE SUCCESS:', data);
        // 2. Sync order_items.status based on the action
        // orders.accepted -> order_items stay 'pending'
        // orders.preparing -> order_items = 'preparing'
        // orders.ready -> order_items = 'ready'
        const ITEM_STATUS_MAP: Record<string, string | null> = {
          accept: null,
          prepare: 'preparing',
          ready: 'ready',
          deliver: null,
          cancel: null // no item change needed
        };
        const nextItemStatus = ITEM_STATUS_MAP[action];
        if (nextItemStatus) {
          console.log('[CAPRICHO KDS] Syncing order_items.status to:', nextItemStatus);
          const {
            error: itemsError
          } = await supabase.from('order_items').update({
            status: nextItemStatus
          }).eq('order_id', orderId);
          if (itemsError) {
            console.error('[CAPRICHO KDS] order_items UPDATE FAILED:', {
              error: itemsError.message,
              details: itemsError.details
            });
          } else {
            console.log('[CAPRICHO KDS] order_items UPDATE SUCCESS');
          }
        }
        await fetchOrders();
      }
    } catch (err: any) {
      console.error('[CAPRICHO KDS] UNEXPECTED ERROR:', err?.message || err);
      await fetchOrders();
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  const getShortOrderNumber = (orderNumber: string) => {
    if (!orderNumber) return '';
    const parts = orderNumber.split('-');
    return parts[parts.length - 1] || orderNumber;
  };
  const getWaitMinutes = (createdAt: string) => {
    return Math.floor((now.getTime() - new Date(createdAt).getTime()) / 60000);
  };
  const columns = [{
    id: 'nuevos',
    title: 'NUEVOS',
    icon: Clock,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
    filter: (o: CaprichoOrder) => o.status === 'new',
    action: 'accept' as const,
    actionLabel: '✅ ACEPTAR PEDIDO',
    actionClass: 'bg-[#8B5E3C] hover:bg-[#A0704A] text-white'
  }, {
    id: 'aceptados',
    title: 'ACEPTADOS',
    icon: CheckCircle2,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/20',
    filter: (o: CaprichoOrder) => o.status === 'accepted',
    action: 'prepare' as const,
    actionLabel: '🔥 EMPEZAR A PREPARAR',
    actionClass: 'bg-amber-500 hover:bg-amber-400 text-black'
  }, {
    id: 'preparando',
    title: 'PREPARANDO',
    icon: ChefHat,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
    filter: (o: CaprichoOrder) => o.status === 'preparing',
    action: 'ready' as const,
    actionLabel: '📦 MARCAR COMO LISTO',
    actionClass: 'bg-emerald-600 hover:bg-emerald-500 text-white'
  }, {
    id: 'listos',
    title: 'LISTOS',
    icon: CheckCircle2,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    filter: (o: CaprichoOrder) => o.status === 'ready',
    action: 'deliver' as const,
    actionLabel: '🚀 ENTREGAR PEDIDO',
    actionClass: 'bg-blue-600 hover:bg-blue-500 text-white'
  }];
  // Only count orders that are truly in-process for Capricho
  const activeOrders = orders.filter(o => ['new', 'accepted', 'preparing', 'ready'].includes(o.status));
  if (loading && orders.length === 0) {
    return <div className="flex-1 flex items-center justify-center min-h-[50vh]" data-id="element-161">
        <div className="w-12 h-12 border-4 border-[#8B5E3C]/30 border-t-[#8B5E3C] rounded-full animate-spin" data-id="element-162" />
      </div>;
  }
  return <div className="flex-1 p-4 sm:p-6 overflow-x-auto bg-[#FBF7F2] min-h-screen" data-id="element-163">
      {/* Active count */}
      <div className="mb-4 flex items-center gap-3" data-id="element-164">
        <h1 className="text-xl font-black text-[#3D2B1F]" data-id="element-165">Cocina Capricho</h1>
        <span className="bg-[#8B5E3C] text-white px-3 py-1 rounded-full text-sm font-bold" data-id="element-166">
          {activeOrders.length} activos
        </span>
      </div>

      <div className="flex gap-5 min-w-max h-full items-start" data-id="element-167">
        {columns.map(col => {
        const colOrders = activeOrders.filter(col.filter).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        return <div key={col.id} className="w-[320px] flex flex-col h-[calc(100vh-160px)]" data-id="element-168">
              {/* Column Header */}
              <div className={cn('p-4 rounded-t-2xl border-x border-t flex items-center justify-between', col.bgColor, col.borderColor)} data-id="element-169">
                <div className="flex items-center gap-2" data-id="element-170">
                  <col.icon className={cn('w-5 h-5', col.color)} data-id="element-171" />
                  <h2 className={cn('font-black tracking-wider text-sm', col.color)} data-id="element-172">
                    {col.title}
                  </h2>
                </div>
                <span className={cn('px-2.5 py-0.5 rounded-full text-sm font-bold bg-white/50', col.color)} data-id="element-173">
                  {colOrders.length}
                </span>
              </div>

              {/* Orders List */}
              <div className={cn('flex-1 overflow-y-auto p-3 space-y-4 border-x border-b rounded-b-2xl bg-white/50', col.borderColor)} data-id="element-174">
                {colOrders.length === 0 ? <div className="h-32 flex flex-col items-center justify-center text-[#8B7355]/40" data-id="element-175">
                    <col.icon className="w-8 h-8 mb-2 opacity-20" data-id="element-176" />
                    <p className="text-sm font-medium" data-id="element-177">Sin pedidos</p>
                  </div> : colOrders.map(order => {
              const isUpdating = updatingOrders.has(order.id);
              const waitMins = getWaitMinutes(order.created_at);
              return <div key={order.id} className="bg-white rounded-xl shadow-sm border border-[#E8DDD0] overflow-hidden flex flex-col" data-id="element-178">
                        {/* Card Header */}
                        <div className="p-3 border-b border-[#E8DDD0] flex justify-between items-start bg-[#FBF7F2]" data-id="element-179">
                          <div data-id="element-180">
                            <div className="flex items-center gap-2 mb-1" data-id="element-181">
                              <span className="text-lg font-black text-[#3D2B1F]" data-id="element-182">
                                #{getShortOrderNumber(order.order_number)}
                              </span>
                              {order.delivery_type === 'delivery' ? <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 uppercase" data-id="element-183">
                                  <Bike className="w-3 h-3" data-id="element-184" /> Delivery
                                </span> : <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 uppercase" data-id="element-185">
                                  <ShoppingBag className="w-3 h-3" data-id="element-186" /> Llevar
                                </span>}
                            </div>
                            <p className="font-bold text-[#3D2B1F]" data-id="element-187">
                              {order.customer_name}
                            </p>
                            {order.customer_phone && <p className="text-xs text-[#8B7355]" data-id="element-188">
                                {order.customer_phone}
                              </p>}
                          </div>
                          <div className="flex flex-col items-end gap-1" data-id="element-189">
                            <span className={cn('text-lg font-black tabular-nums', waitMins >= 15 ? 'text-red-500 animate-pulse' : waitMins >= 10 ? 'text-amber-500' : 'text-[#8B5E3C]')} data-id="element-190">
                              {waitMins}m
                            </span>
                            {order.ticket_image_url && <button onClick={() => setTicketPreview(order.ticket_image_url)} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#E8DDD0] hover:bg-[#D4C4B0] text-[#8B5E3C] transition-colors" title="Ver ticket" data-id="element-191">
                                <Eye className="w-3.5 h-3.5" data-id="element-192" />
                                <span className="text-[10px] font-bold" data-id="element-193">
                                  Ticket
                                </span>
                              </button>}
                          </div>
                        </div>

                        {/* Delivery Info */}
                        {(order.delivery_date || order.delivery_time_slot) && <div className="px-3 py-2 bg-[#F5E9DA]/50 border-b border-[#E8DDD0] text-xs font-medium text-[#8B7355] flex flex-col gap-0.5" data-id="element-194">
                            {order.delivery_date && <span data-id="element-195">📅 {order.delivery_date}</span>}
                            {order.delivery_time_slot && <span data-id="element-196">🕐 {order.delivery_time_slot}</span>}
                          </div>}

                        {/* Items */}
                        <div className="p-3 flex-1 space-y-3" data-id="element-197">
                          {order.items.map(item => <div key={item.id} className="text-sm" data-id="element-198">
                              <div className="font-bold text-[#3D2B1F] flex items-start gap-2" data-id="element-199">
                                <span className="bg-[#E8DDD0] text-[#8B5E3C] px-1.5 rounded text-xs py-0.5 mt-0.5" data-id="element-200">
                                  {item.quantity}x
                                </span>
                                <span data-id="element-201">{item.item_name_snapshot}</span>
                              </div>
                              {item.options && item.options.length > 0 && <div className="mt-1 ml-7 space-y-0.5" data-id="element-202">
                                  {item.options.map((opt, idx) => <div key={idx} className="text-xs text-[#8B5E3C] font-medium flex items-center gap-1" data-id="element-203">
                                      <span className="text-[#C8A96A]" data-id="element-204">+</span>{' '}
                                      {opt.option_name}
                                      {opt.price_delta > 0 && <span className="text-[#8B7355]/50 ml-1" data-id="element-205">
                                          +₡{opt.price_delta.toLocaleString()}
                                        </span>}
                                    </div>)}
                                </div>}
                              {item.notes && <p className="text-xs text-red-600 italic mt-1 ml-7 bg-red-50 p-1 rounded" data-id="element-206">
                                  📝 {item.notes}
                                </p>}
                            </div>)}

                          {order.notes && <div className="mt-2 pt-2 border-t border-[#E8DDD0] border-dashed" data-id="element-207">
                              <p className="text-xs text-red-600 font-medium bg-red-50 p-1.5 rounded" data-id="element-208">
                                📝 Nota: {order.notes}
                              </p>
                            </div>}
                        </div>

                        {/* Total */}
                        <div className="px-3 py-2 border-t border-[#E8DDD0] flex justify-between items-center" data-id="element-209">
                          <span className="text-xs font-bold text-[#8B7355]" data-id="element-210">
                            Total
                          </span>
                          <span className="font-black text-[#8B5E3C]" data-id="element-211">
                            ₡{order.total.toLocaleString()}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="p-2 border-t border-[#E8DDD0] bg-[#FBF7F2] space-y-2" data-id="element-212">
                          <button onClick={() => handleAction(order.id, col.action)} disabled={isUpdating} className={cn('w-full py-3 rounded-lg text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2', col.actionClass)} data-id="element-213">
                            {isUpdating ? <span className="flex items-center gap-2" data-id="element-214">
                                <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" data-id="element-215" />
                                Procesando...
                              </span> : col.actionLabel}
                          </button>

                          {col.id !== 'listos' && <button onClick={() => handleAction(order.id, 'cancel')} disabled={isUpdating} className="w-full py-1 text-xs font-semibold text-[#8B7355]/60 hover:text-red-500 transition-colors" data-id="element-216">
                              Cancelar pedido
                            </button>}
                        </div>
                      </div>;
            })}
              </div>
            </div>;
      })}
      </div>

      {/* Ticket Preview Modal */}
      {ticketPreview && <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setTicketPreview(null)} data-id="element-217">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-218">
            <div className="flex items-center justify-between p-3 border-b border-[#E8DDD0] bg-[#FBF7F2]" data-id="element-219">
              <span className="text-sm font-black text-[#3D2B1F]" data-id="element-220">
                📄 Ticket del pedido
              </span>
              <button onClick={() => setTicketPreview(null)} className="w-8 h-8 rounded-lg bg-[#E8DDD0] hover:bg-[#D4C4B0] flex items-center justify-center transition-colors" data-id="element-221">
                <X className="w-4 h-4 text-[#8B5E3C]" data-id="element-222" />
              </button>
            </div>
            <div className="overflow-auto max-h-[80vh] p-2 bg-white" data-id="element-223">
              <img src={ticketPreview} alt="Ticket del pedido" className="w-full h-auto rounded-lg" data-id="element-224" />
            </div>
          </div>
        </div>}
    </div>;
}